"use client"

import { useMemo, useState, useEffect } from "react"
import Image from "next/image"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Download, Copy, Link, Mail, Phone, Wifi, CreditCard, MapPin } from "lucide-react"
import { toast } from "sonner"
import QRCode from "qrcode"

type QrType = "url" | "text" | "email" | "phone" | "wifi" | "vcard" | "location"

interface WifiData {
  ssid: string
  password: string
  encryption: "WPA" | "WEP" | "nopass"
  hidden: boolean
}

interface VCardData {
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  title: string
  website: string
}

interface LocationData {
  latitude: string
  longitude: string
  name: string
}

export function QrCodeGenerator() {
  const [qrType, setQrType] = useState<QrType>("url")
  const [content, setContent] = useState("https://example.com")
  const [size, setSize] = useState(256)
  const [fgColor, setFgColor] = useState("#000000")
  const [bgColor, setBgColor] = useState("#ffffff")
  const [errorLevel, setErrorLevel] = useState<"L" | "M" | "Q" | "H">("M")
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  // Type-specific data
  const [wifiData, setWifiData] = useState<WifiData>({
    ssid: "",
    password: "",
    encryption: "WPA",
    hidden: false,
  })
  const [vcardData, setVcardData] = useState<VCardData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    website: "",
  })
  const [locationData, setLocationData] = useState<LocationData>({
    latitude: "",
    longitude: "",
    name: "",
  })
  const [emailData, setEmailData] = useState({ to: "", subject: "", body: "" })
  const [phoneNumber, setPhoneNumber] = useState("")

  const qrContent = useMemo(() => {
    switch (qrType) {
      case "url":
      case "text":
        return content
      case "email":
        let mailto = `mailto:${emailData.to}`
        const params: string[] = []
        if (emailData.subject) params.push(`subject=${encodeURIComponent(emailData.subject)}`)
        if (emailData.body) params.push(`body=${encodeURIComponent(emailData.body)}`)
        if (params.length) mailto += `?${params.join("&")}`
        return mailto
      case "phone":
        return `tel:${phoneNumber}`
      case "wifi":
        return `WIFI:T:${wifiData.encryption};S:${wifiData.ssid};P:${wifiData.password};H:${wifiData.hidden ? "true" : "false"};;`
      case "vcard":
        return [
          "BEGIN:VCARD",
          "VERSION:3.0",
          `N:${vcardData.lastName};${vcardData.firstName}`,
          `FN:${vcardData.firstName} ${vcardData.lastName}`,
          vcardData.company && `ORG:${vcardData.company}`,
          vcardData.title && `TITLE:${vcardData.title}`,
          vcardData.email && `EMAIL:${vcardData.email}`,
          vcardData.phone && `TEL:${vcardData.phone}`,
          vcardData.website && `URL:${vcardData.website}`,
          "END:VCARD",
        ].filter(Boolean).join("\n")
      case "location":
        return `geo:${locationData.latitude},${locationData.longitude}${locationData.name ? `?q=${encodeURIComponent(locationData.name)}` : ""}`
      default:
        return content
    }
  }, [qrType, content, wifiData, vcardData, locationData, emailData, phoneNumber])

  useEffect(() => {
    if (!qrContent) return

    let active = true
    QRCode.toDataURL(qrContent, {
      width: size,
      margin: 2,
      color: {
        dark: fgColor,
        light: bgColor,
      },
      errorCorrectionLevel: errorLevel,
    })
      .then((url) => {
        if (active) setQrDataUrl(url)
      })
      .catch(() => {
        if (active) setQrDataUrl(null)
      })

    return () => {
      active = false
    }
  }, [qrContent, size, fgColor, bgColor, errorLevel])

  const visibleQrDataUrl = qrContent ? qrDataUrl : null

  const downloadQr = (format: "png" | "svg") => {
    if (!visibleQrDataUrl) return

    if (format === "png") {
      const link = document.createElement("a")
      link.download = "qrcode.png"
      link.href = visibleQrDataUrl
      link.click()
    } else {
      // Generate SVG
      QRCode.toString(qrContent, {
        type: "svg",
        width: size,
        margin: 2,
        color: {
          dark: fgColor,
          light: bgColor,
        },
        errorCorrectionLevel: errorLevel,
      }).then((svg) => {
        const blob = new Blob([svg], { type: "image/svg+xml" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.download = "qrcode.svg"
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
      })
    }
    toast.success(`QR code downloaded as ${format.toUpperCase()}`)
  }

  const copyQrToClipboard = async () => {
    if (!qrDataUrl) return
    try {
      const response = await fetch(qrDataUrl)
      const blob = await response.blob()
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ])
      toast.success("QR code copied to clipboard")
    } catch {
      toast.error("Failed to copy to clipboard")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>QR Code Generator</CardTitle>
        <CardDescription>
          Generate QR codes for URLs, text, WiFi, contact cards, and more. Download as PNG or SVG.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left: Options */}
          <div className="space-y-4">
            {/* QR Type */}
            <div className="space-y-2">
              <Label>QR Code Type</Label>
              <Tabs value={qrType} onValueChange={(v) => setQrType(v as QrType)}>
                <TabsList className="grid grid-cols-4 h-auto">
                  <TabsTrigger value="url" className="flex flex-col gap-1 py-2">
                    <Link className="h-4 w-4" />
                    <span className="text-xs">URL</span>
                  </TabsTrigger>
                  <TabsTrigger value="text" className="flex flex-col gap-1 py-2">
                    <span className="text-sm font-mono">Aa</span>
                    <span className="text-xs">Text</span>
                  </TabsTrigger>
                  <TabsTrigger value="email" className="flex flex-col gap-1 py-2">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs">Email</span>
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="flex flex-col gap-1 py-2">
                    <Phone className="h-4 w-4" />
                    <span className="text-xs">Phone</span>
                  </TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-3 h-auto mt-1">
                  <TabsTrigger value="wifi" className="flex flex-col gap-1 py-2">
                    <Wifi className="h-4 w-4" />
                    <span className="text-xs">WiFi</span>
                  </TabsTrigger>
                  <TabsTrigger value="vcard" className="flex flex-col gap-1 py-2">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xs">vCard</span>
                  </TabsTrigger>
                  <TabsTrigger value="location" className="flex flex-col gap-1 py-2">
                    <MapPin className="h-4 w-4" />
                    <span className="text-xs">Location</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Type-specific inputs */}
            <div className="space-y-3">
              {(qrType === "url" || qrType === "text") && (
                <div className="space-y-2">
                  <Label>{qrType === "url" ? "URL" : "Text"}</Label>
                  {qrType === "url" ? (
                    <Input
                      placeholder="https://example.com"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                    />
                  ) : (
                    <Textarea
                      placeholder="Enter your text..."
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="min-h-[100px]"
                    />
                  )}
                </div>
              )}

              {qrType === "email" && (
                <>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="hello@example.com"
                      value={emailData.to}
                      onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subject (optional)</Label>
                    <Input
                      placeholder="Subject line"
                      value={emailData.subject}
                      onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Body (optional)</Label>
                    <Textarea
                      placeholder="Email body..."
                      value={emailData.body}
                      onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                    />
                  </div>
                </>
              )}

              {qrType === "phone" && (
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+1-234-567-8900"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              )}

              {qrType === "wifi" && (
                <>
                  <div className="space-y-2">
                    <Label>Network Name (SSID)</Label>
                    <Input
                      placeholder="WiFi Network"
                      value={wifiData.ssid}
                      onChange={(e) => setWifiData({ ...wifiData, ssid: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      placeholder="WiFi Password"
                      value={wifiData.password}
                      onChange={(e) => setWifiData({ ...wifiData, password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Security</Label>
                    <Select
                      value={wifiData.encryption}
                      onValueChange={(v: "WPA" | "WEP" | "nopass") => setWifiData({ ...wifiData, encryption: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WPA">WPA/WPA2</SelectItem>
                        <SelectItem value="WEP">WEP</SelectItem>
                        <SelectItem value="nopass">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {qrType === "vcard" && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={vcardData.firstName}
                      onChange={(e) => setVcardData({ ...vcardData, firstName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={vcardData.lastName}
                      onChange={(e) => setVcardData({ ...vcardData, lastName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={vcardData.email}
                      onChange={(e) => setVcardData({ ...vcardData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      value={vcardData.phone}
                      onChange={(e) => setVcardData({ ...vcardData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input
                      value={vcardData.company}
                      onChange={(e) => setVcardData({ ...vcardData, company: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input
                      value={vcardData.title}
                      onChange={(e) => setVcardData({ ...vcardData, title: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {qrType === "location" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Latitude</Label>
                      <Input
                        placeholder="40.7128"
                        value={locationData.latitude}
                        onChange={(e) => setLocationData({ ...locationData, latitude: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Longitude</Label>
                      <Input
                        placeholder="-74.0060"
                        value={locationData.longitude}
                        onChange={(e) => setLocationData({ ...locationData, longitude: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location Name (optional)</Label>
                    <Input
                      placeholder="New York City"
                      value={locationData.name}
                      onChange={(e) => setLocationData({ ...locationData, name: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Customization */}
            <div className="space-y-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Size: {size}px</Label>
                <Slider
                  value={[size]}
                  min={128}
                  max={512}
                  step={32}
                  onValueChange={(v) => setSize(v[0])}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Foreground</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Background</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Error Correction</Label>
                <Select value={errorLevel} onValueChange={(v: "L" | "M" | "Q" | "H") => setErrorLevel(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Low (7%)</SelectItem>
                    <SelectItem value="M">Medium (15%)</SelectItem>
                    <SelectItem value="Q">Quartile (25%)</SelectItem>
                    <SelectItem value="H">High (30%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="space-y-4">
            <Label>Preview</Label>
            <div className="border rounded-lg p-6 flex items-center justify-center min-h-[300px] bg-white">
              {visibleQrDataUrl ? (
                <Image
                  src={visibleQrDataUrl}
                  alt="Generated QR code preview"
                  width={size}
                  height={size}
                  unoptimized
                  style={{ width: size, height: size, maxWidth: "100%" }}
                />
              ) : (
                <p className="text-muted-foreground">Enter content to generate QR code</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => downloadQr("png")} disabled={!visibleQrDataUrl} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Download PNG
              </Button>
              <Button variant="outline" onClick={() => downloadQr("svg")} disabled={!visibleQrDataUrl} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Download SVG
              </Button>
              <Button variant="outline" size="icon" onClick={copyQrToClipboard} disabled={!visibleQrDataUrl}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
